import { useNavigate } from "react-router-dom";
import { Handshake, RefreshCw } from "lucide-react";
import { Button, EmptyState, Pill, SectionTitle, Spinner, toast } from "@/ui";
import { useSession } from "../_shims";
import { SECTION_HINTS, SECTION_LABELS, groupCards } from "../lib/cards";
import { useMatchCards, useRefreshMatches } from "../lib/hooks";
import { MatchModal } from "../components/MatchModal";
import { MatchRow } from "../components/MatchRow";
import { PageFrame } from "../components/PageFrame";
import { useMatchOpener } from "../components/useMatchOpener";
import s from "../network.module.css";

export const MATCHES_EMPTY_COPY = "No introductions yet. Add what you need and what you offer, and connect with people on Hub.";

export function MatchesPage() {
  const navigate = useNavigate();
  const { isSignedIn } = useSession();
  const cards = useMatchCards();
  const refresh = useRefreshMatches();
  const opener = useMatchOpener();

  const onRefresh = async () => {
    try {
      const res = await refresh.mutateAsync();
      toast(res.matchesFound > 0 ? `${res.matchesFound} new introduction${res.matchesFound === 1 ? "" : "s"} found.` : "No new introductions right now.", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not refresh matches.", "danger");
    }
  };

  const groups = groupCards(cards.data?.cards ?? []);

  return (
    <PageFrame
      title="Introductions"
      subtitle={cards.data?.source === "cache" ? (isSignedIn ? "Showing your last synced introductions; the server is unreachable." : "Signed out: showing cached introductions.") : "People who need something and people who can help, matched across your network."}
      actions={
        <Button icon={<RefreshCw size={14} />} onClick={onRefresh} loading={refresh.isPending} disabled={!isSignedIn} title={isSignedIn ? "Look for new matches now" : "Sign in to refresh"}>
          Refresh introductions
        </Button>
      }
    >
      {cards.isLoading ? (
        <div className="u-row" style={{ justifyContent: "center", padding: 40 }}>
          <Spinner />
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<Handshake size={32} />}
          title="Nothing here yet"
          action={
            <div className="u-row">
              <Button variant="primary" onClick={() => navigate("/needs?new=1")}>
                Add a need
              </Button>
              <Button onClick={() => navigate("/services?new=1")}>Add an offer</Button>
              <Button variant="ghost" onClick={() => navigate("/network")}>
                Find people on Hub
              </Button>
            </div>
          }
        >
          {MATCHES_EMPTY_COPY}
        </EmptyState>
      ) : (
        groups.map((g) => (
          <section key={g.section} className={s.section} aria-label={SECTION_LABELS[g.section]} data-testid={`section-${g.section}`}>
            <SectionTitle action={<Pill tone={g.section === "waiting_on_you" ? "accent" : "neutral"}>{g.cards.length}</Pill>}>{SECTION_LABELS[g.section]}</SectionTitle>
            <div className={s.sectionHint}>{SECTION_HINTS[g.section]}</div>
            <div className={s.stack}>
              {g.cards.map((c) => (
                <MatchRow key={c.matchId} card={c} onOpen={opener.open} onAction={opener.act} />
              ))}
            </div>
          </section>
        ))
      )}
      <MatchModal card={opener.modal?.card ?? null} initialAction={opener.modal?.action ?? null} onClose={opener.close} />
    </PageFrame>
  );
}
